import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import debounce from "lodash.debounce";
import DashboardNavbar from "../DashboardNavbar";
import Pagination from "../../Pagination";

function UserManagement() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState([]);
  const [buttonData, setButtonData] = useState([]);
  const [showButtonData, setShowButtonData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState(""); // Sorting field
  const [sortOrder, setSortOrder] = useState("asc"); // Sorting order: "asc" or "desc"

  const debouncedSearch = debounce((searchQuery) => {
    setSearchQuery(searchQuery);
  }, 500);

  const handleSearch = (e) => {
    debouncedSearch(e.target.value);
    setSearch(e.target.value);
  };

  const handleAdd = (event) => {
    event.preventDefault();
    navigate("/add_user");
  };

  const fetchData = async () => {
    setLoading(true);
    // Retrieve client ID and created_by (user ID) from localStorage
    const storedClient = localStorage.getItem("client");

    // Debugging: Log stored values
    // console.log("Stored Client ID:", storedClient);

    // Ensure valid client ID
    const client_id =
      storedClient && !isNaN(storedClient) ? Number(storedClient) : null;

    if (!client_id) {
      alert("Client ID  missing.");
      return;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/auth/users?client_id=${client_id}`
      );
      if (Array.isArray(response.data)) {
        setData(response.data); // Access the array inside the "data" key
      } else {
        console.error("Data is not an array:", response.data);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const fetchButtonData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/auth/deactivated-users/`
        );
        setButtonData(response.data);
      } catch (error) {
        console.log(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchButtonData();
  }, []);

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm(
      "Are you sure that you want to deactivate this User?"
    );
    if (isConfirmed) {
      try {
        // Send a PATCH request to update the user with is_active: false
        await axios.patch(
          `${process.env.REACT_APP_API_BASE_URL}/auth/update/${id}/`,
          { is_active: false }, // Send the is_active: false in the request body
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        fetchData();
        window.location.reload();
      } catch (error) {
        console.log(error.message);
      }
    }
  };

  const handleActivate = (id) => {
    axios
      .patch(`${process.env.REACT_APP_API_BASE_URL}/auth/update/${id}/`, {
        is_active: true,
      })
      .then((response) => {
        // console.log("User activated successfully:", response.data);

        alert("User Activated Sucessfully");
        window.location.reload();
        // Optionally, you can refresh the data or update the state here
        // e.g., fetchData();
      })
      .catch((error) => {
        // Check if the error response and the message exist
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          alert(error.response.data.message); // Show the backend error message
        } else {
          alert("An unexpected error occurred. Please try again."); // Fallback error message
        }
        console.error(error);
      });
  };

  if (loading) {
    return (
      <div
        className="container d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div>
          <div
            className="spinner-border text-dark"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <h3 className="text-center text-dark">&nbsp;Data is Fetching.... </h3>
      </div>
    );
  }

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
  };

  const getSortArrow = (field) => {
    if (sortField !== field) return "▲▼"; // Display both arrows by default
    return sortOrder === "asc" ? "▲" : "▼"; // Show the active arrow based on sorting order
  };

  const sortedItems = [...data].sort((a, b) => {
    if (sortField) {
      if (sortOrder === "asc") {
        return a[sortField] > b[sortField] ? 1 : -1;
      } else {
        return a[sortField] < b[sortField] ? 1 : -1;
      }
    }
    return 0;
  });

  const filteredItems = showButtonData
    ? buttonData.filter(
        (item) =>
          (item.id?.toString() || "").includes(searchQuery) ||
          (item.first_name?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.last_name?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.email?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.phone_number?.toString() || "").includes(searchQuery) ||
          (item.organization?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.designation?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.role?.toLowerCase() || "").includes(searchQuery.toLowerCase())
      )
    : sortedItems.filter(
        (item) =>
          (item.id?.toString() || "").includes(searchQuery) ||
          (item.first_name?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.last_name?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.email?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.phone_number?.toString() || "").includes(searchQuery) ||
          (item.organization?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.designation?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.role?.toLowerCase() || "").includes(searchQuery.toLowerCase())
      );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const handleDownload = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/auth/download-users/`,
        {
          method: "GET",
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Screenify_User_Data.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        console.error("Download failed");
      }
    } catch (error) {
      console.error("An error occurred during download", error);
    }
  };

  return (
    <>
      <DashboardNavbar />
      <div className="container py-3 main-content">
        <div className="p-2 mb-1 rounded">
          <h3 className="text-center font_header">User Dashboard</h3>

          <div className="row align-items-center mt-1 mb-4">
            <div className="col-12 col-lg-4 mb-2 mb-lg-0">
              <div className="search">
                <form className="form-inline text-dark fw-bold">
                  <div className="col-lg-7 col-md-5">
                    <label htmlFor="search">Search:</label>
                    <input
                      className="form-control mt-1 border-dark text-dark"
                      type="search"
                      title="Search"
                      aria-label="Search"
                      onChange={handleSearch}
                      value={search}
                    />
                  </div>
                </form>
              </div>
            </div>
            <div className="col-12 col-lg-8 text-lg-end mt-3">
              <button
                className="btn btn-sm btn-outline-info mb-2 mx-2"
                title="Add User"
                onClick={handleAdd}
              >
                Add User
              </button>

              <button
                className="btn btn-sm btn-outline-info mb-2"
                onClick={() => setShowButtonData(!showButtonData)}
              >
                {showButtonData ? "Show Active User" : "Show Deactivated User"}
              </button>
              <button
                className="btn btn-sm btn-outline-info mb-2 mx-2"
                title="Download User Data"
                onClick={handleDownload}
              >
                Download
              </button>
            </div>
          </div>
        </div>
        <div className="row">
          <div
            className="table-responsive table-wrapper text-center"
            style={{ flex: 1 }}
          >
            <table
              className="table table-hover table-bordered border-dark"
              style={{
                tableLayout: "fixed",
                width: "100%",
                minWidth: "800px",
              }}
            >
              <thead className="tb_header border-dark">
                <tr>
                  <th
                    className={sortField === "id" ? "sorted" : ""}
                    onClick={() => handleSort("id")}
                    style={{
                      width: "100px",
                      cursor: "pointer",
                    }}
                  >
                    ID
                    {getSortArrow("id")}
                  </th>
                  <th
                    onClick={() => handleSort("first_name")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    First&nbsp;Name
                    <span className="sort-arrow">
                      {getSortArrow("first_name")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("last_name")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Last&nbsp;Name
                    {getSortArrow("last_name")}
                  </th>
                  <th
                    onClick={() => handleSort("email")}
                    style={{ width: "250px", cursor: "pointer" }}
                  >
                    Email
                    {getSortArrow("email")}
                  </th>
                  <th
                    onClick={() => handleSort("phone_number")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Phone&nbsp;Number
                    {getSortArrow("phone_number")}
                  </th>
                  <th
                    onClick={() => handleSort("organization")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Organization
                    {getSortArrow("organization")}
                  </th>
                  <th
                    onClick={() => handleSort("designation")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Designation
                    {getSortArrow("designation")}
                  </th>
                  <th
                    onClick={() => handleSort("role")}
                    style={{ width: "150px", cursor: "pointer" }}
                  >
                    Role
                    {getSortArrow("role")}
                  </th>

                  <th style={{ width: "100px" }}>Actions</th>
                </tr>
              </thead>
              <tbody className="text-center table-light border-dark">
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.first_name}</td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.last_name}
                      </td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.email}
                      </td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.phone_number}
                      </td>
                      <td>{item.organization}</td>
                      <td>{item.designation}</td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.role}
                      </td>
                      {!showButtonData ? (
                        <td>
                          <Link
                            to={`/add_user_copy/${item.id}`}
                            className="copy"
                            title="Copy"
                            data-toggle="tooltip"
                          >
                            <i className="fa fa-copy"></i>
                          </Link>
                          <Link
                            to={`/edit_user/${item.id}`}
                            className="edit"
                            title="Edit"
                            data-toggle="tooltip"
                          >
                            <i className="fa fa-edit ms-1 me-1"></i>
                          </Link>
                          <Link
                            to="#"
                            className="delete"
                            title="Deactivate User"
                            data-toggle="tooltip"
                            onClick={() => handleDelete(item.id)}
                          >
                            <i className="fa fa-trash text-danger"></i>
                          </Link>
                        </td>
                      ) : (
                        <td>
                          <button
                            className="btn btn-outline-success"
                            title="Activate User"
                            onClick={() => handleActivate(item.id)}
                          >
                            <i className="fa-solid fa-trash-arrow-up"></i>
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-start">
                      <h4>No data available</h4>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Pagination Code */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        paginate={(page) => setCurrentPage(page)}
      />
    </>
  );
}

export default UserManagement;
